import type { CardType, VisibleCardViewV1 } from "@fate-kingdom/engine";

import archersArt from "../../assets/alpha/cards/armies/archers.svg";
import bannerArt from "../../assets/alpha/cards/armies/banner.svg";
import engineersArt from "../../assets/alpha/cards/armies/engineers.svg";
import guardArt from "../../assets/alpha/cards/armies/guard.svg";
import ridersArt from "../../assets/alpha/cards/armies/riders.svg";
import scoutsArt from "../../assets/alpha/cards/armies/scouts.svg";
import spearmenArt from "../../assets/alpha/cards/armies/spearmen.svg";
import vanguardArt from "../../assets/alpha/cards/armies/vanguard.svg";
import azureStrategistArt from "../../assets/alpha/cards/commanders/azure-strategist.svg";
import crimsonMarshalArt from "../../assets/alpha/cards/commanders/crimson-marshal.svg";
import coastArt from "../../assets/alpha/cards/lands/coast.svg";
import forestArt from "../../assets/alpha/cards/lands/forest.svg";
import hillsArt from "../../assets/alpha/cards/lands/hills.svg";
import plainsArt from "../../assets/alpha/cards/lands/plains.svg";
import riverArt from "../../assets/alpha/cards/lands/river.svg";

export type AlphaFaction = "neutral" | "assault" | "balanced";
export type AlphaCardArtKey =
  | "plains"
  | "forest"
  | "hills"
  | "river"
  | "coast"
  | "crimson-marshal"
  | "azure-strategist"
  | "vanguard"
  | "archers"
  | "riders"
  | "spearmen"
  | "engineers"
  | "scouts"
  | "guard"
  | "banner"
  | "fallback";

export interface AlphaCardPresentation {
  readonly cardDefinitionId: string;
  readonly displayName: string;
  readonly shortLabel: string;
  readonly flavorText: string;
  readonly artKey: AlphaCardArtKey;
  readonly artUrl?: string;
  readonly artAlt: string;
  readonly cardType: CardType;
  readonly faction: AlphaFaction;
  readonly accent: "land" | "commander" | "army" | "event";
}

const land = (
  cardDefinitionId: string,
  displayName: string,
  artKey: Extract<AlphaCardArtKey, "plains" | "forest" | "hills" | "river" | "coast">,
  artUrl: string,
  artAlt: string,
): AlphaCardPresentation =>
  Object.freeze({
    cardDefinitionId,
    displayName,
    shortLabel: displayName,
    flavorText: "A frontier holding that can grow into a stronger source of local resources.",
    artKey,
    artUrl,
    artAlt,
    cardType: "land",
    faction: "neutral",
    accent: "land",
  });

const commander = (
  cardDefinitionId: string,
  displayName: string,
  faction: Extract<AlphaFaction, "assault" | "balanced">,
  artKey: Extract<AlphaCardArtKey, "crimson-marshal" | "azure-strategist">,
  artUrl: string,
  artAlt: string,
): AlphaCardPresentation =>
  Object.freeze({
    cardDefinitionId,
    displayName,
    shortLabel: displayName,
    flavorText: "A lane leader who enables a stationed garrison to launch normal attacks.",
    artKey,
    artUrl,
    artAlt,
    cardType: "commander",
    faction,
    accent: "commander",
  });

const army = (
  cardDefinitionId: string,
  displayName: string,
  faction: Extract<AlphaFaction, "assault" | "balanced">,
  artKey: Extract<
    AlphaCardArtKey,
    "vanguard" | "archers" | "riders" | "spearmen" | "engineers" | "scouts" | "guard" | "banner"
  >,
  artUrl: string,
  artAlt: string,
): AlphaCardPresentation =>
  Object.freeze({
    cardDefinitionId,
    displayName,
    shortLabel: displayName,
    flavorText:
      "Station this Army on any active friendly Land. It guards and requires local Upkeep.",
    artKey,
    artUrl,
    artAlt,
    cardType: "army",
    faction,
    accent: "army",
  });

/**
 * Web-only aliases and local fallback art for the public-beta fixture. This map is deliberately not imported
 * by card data, the engine, replay handling, or bot policy.
 */
export const ALPHA_CARD_PRESENTATION: Readonly<Record<string, AlphaCardPresentation>> =
  Object.freeze({
    "card.core-battle-match-plains": land(
      "card.core-battle-match-plains",
      "Plains",
      "plains",
      plainsArt,
      "Golden plains with a road and distant settlement",
    ),
    "card.core-battle-match-forest": land(
      "card.core-battle-match-forest",
      "Forest",
      "forest",
      forestArt,
      "Dense forest canopy with a watchtower",
    ),
    "card.core-battle-match-hills": land(
      "card.core-battle-match-hills",
      "Hills",
      "hills",
      hillsArt,
      "Layered ochre hills and a hill fort",
    ),
    "card.core-battle-match-river": land(
      "card.core-battle-match-river",
      "River",
      "river",
      riverArt,
      "River crossing with a stone bridge",
    ),
    "card.core-battle-match-coast": land(
      "card.core-battle-match-coast",
      "Coast",
      "coast",
      coastArt,
      "Sea cliffs and a small harbour",
    ),
    "card.core-battle-match-assault-commander": commander(
      "card.core-battle-match-assault-commander",
      "Crimson Marshal",
      "assault",
      "crimson-marshal",
      crimsonMarshalArt,
      "Crimson Marshal in red armour carrying a banner",
    ),
    "card.core-battle-match-balanced-commander": commander(
      "card.core-battle-match-balanced-commander",
      "Azure Strategist",
      "balanced",
      "azure-strategist",
      azureStrategistArt,
      "Azure Strategist with a command staff and campaign map",
    ),
    "card.core-battle-match-assault-army-1": army(
      "card.core-battle-match-assault-army-1",
      "Crimson Vanguard",
      "assault",
      "vanguard",
      vanguardArt,
      "Crimson vanguard infantry with shields",
    ),
    "card.core-battle-match-assault-army-2": army(
      "card.core-battle-match-assault-army-2",
      "Ember Archers",
      "assault",
      "archers",
      archersArt,
      "Ember archers drawing bows",
    ),
    "card.core-battle-match-assault-army-3": army(
      "card.core-battle-match-assault-army-3",
      "Iron Riders",
      "assault",
      "riders",
      ridersArt,
      "Iron riders crossing an open field",
    ),
    "card.core-battle-match-assault-army-4": army(
      "card.core-battle-match-assault-army-4",
      "Pike Cohort",
      "assault",
      "spearmen",
      spearmenArt,
      "Pike cohort holding a spear line",
    ),
    "card.core-battle-match-assault-army-5": army(
      "card.core-battle-match-assault-army-5",
      "Siege Crew",
      "assault",
      "engineers",
      engineersArt,
      "Siege crew raising a field frame",
    ),
    "card.core-battle-match-assault-army-6": army(
      "card.core-battle-match-assault-army-6",
      "Red Scouts",
      "assault",
      "scouts",
      scoutsArt,
      "Red scouts watching a frontier",
    ),
    "card.core-battle-match-assault-army-7": army(
      "card.core-battle-match-assault-army-7",
      "Bastion Breakers",
      "assault",
      "guard",
      guardArt,
      "Heavy assault guard behind shields",
    ),
    "card.core-battle-match-assault-army-8": army(
      "card.core-battle-match-assault-army-8",
      "War Banner Company",
      "assault",
      "banner",
      bannerArt,
      "Banner company beneath crimson standards",
    ),
    "card.core-battle-match-balanced-army-1": army(
      "card.core-battle-match-balanced-army-1",
      "Azure Guard",
      "balanced",
      "guard",
      guardArt,
      "Azure guard behind tall shields",
    ),
    "card.core-battle-match-balanced-army-2": army(
      "card.core-battle-match-balanced-army-2",
      "Longbow Wardens",
      "balanced",
      "archers",
      archersArt,
      "Longbow wardens drawing bows",
    ),
    "card.core-battle-match-balanced-army-3": army(
      "card.core-battle-match-balanced-army-3",
      "River Riders",
      "balanced",
      "riders",
      ridersArt,
      "River riders crossing an open field",
    ),
    "card.core-battle-match-balanced-army-4": army(
      "card.core-battle-match-balanced-army-4",
      "Shield Spearmen",
      "balanced",
      "spearmen",
      spearmenArt,
      "Shield spearmen holding a defensive line",
    ),
    "card.core-battle-match-balanced-army-5": army(
      "card.core-battle-match-balanced-army-5",
      "Field Engineers",
      "balanced",
      "engineers",
      engineersArt,
      "Field engineers raising a siege frame",
    ),
    "card.core-battle-match-balanced-army-6": army(
      "card.core-battle-match-balanced-army-6",
      "Dawn Scouts",
      "balanced",
      "scouts",
      scoutsArt,
      "Dawn scouts watching a frontier",
    ),
    "card.core-battle-match-balanced-army-7": army(
      "card.core-battle-match-balanced-army-7",
      "Ivory Bulwark",
      "balanced",
      "vanguard",
      vanguardArt,
      "Ivory bulwark infantry with shields",
    ),
    "card.core-battle-match-balanced-army-8": army(
      "card.core-battle-match-balanced-army-8",
      "Concord Banner",
      "balanced",
      "banner",
      bannerArt,
      "Concord banner company beneath azure standards",
    ),
  });

function fallbackPresentation(
  card: Pick<VisibleCardViewV1, "cardDefinitionId" | "name" | "type">,
): AlphaCardPresentation {
  return Object.freeze({
    cardDefinitionId: card.cardDefinitionId,
    displayName: card.name.replace(/^TEST ONLY\s*/u, ""),
    shortLabel: card.name.replace(/^TEST ONLY\s*/u, ""),
    flavorText: "Public-beta card presentation is unavailable for this definition.",
    artKey: "fallback",
    artAlt: "A parchment card marked with its card type",
    cardType: card.type,
    faction: "neutral",
    accent:
      card.type === "land"
        ? "land"
        : card.type === "commander"
          ? "commander"
          : card.type === "event"
            ? "event"
            : "army",
  });
}

export function getAlphaCardPresentation(
  card: Pick<VisibleCardViewV1, "cardDefinitionId" | "name" | "type">,
): AlphaCardPresentation {
  return ALPHA_CARD_PRESENTATION[card.cardDefinitionId] ?? fallbackPresentation(card);
}

export function getAlphaDisplayName(
  card: Pick<VisibleCardViewV1, "cardDefinitionId" | "name" | "type">,
): string {
  return getAlphaCardPresentation(card).displayName;
}
